import { useEffect, useState } from "react";
import { Alert, Box, Chip, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CableOutlinedIcon from "@mui/icons-material/CableOutlined";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import {
  calcularExcedenteFibra,
  usePreciosConceptosVisita,
} from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/usePreciosConceptosVisita";
import {
  useRefrescarFotosTrasladoLive,
  useSetFotosTrasladoLive,
} from "@/erp/tecnico/orden-trabajo/shared/components/tables/fotos/traslado/fotos-traslado.actions";
import { aprobarExcedenteFibra, registrarExcedenteFibra } from "./excedente-fibra.actions";
import { ExcedenteFibra, excedenteFibraPendiente, moneyExcedente } from "./excedente-fibra.utils";

const ACCENT = "#00695c";

export type ModoExcedenteFibra = "tecnico" | "activaciones" | "lectura";

type Props = {
  idOrdenTrabajo?: string;
  /** Excedente guardado (idealmente el estado en vivo). */
  excedente: ExcedenteFibra | null;
  modo: ModoExcedenteFibra;
  /** Técnico: hay FIBRA PLANA en los materiales del traslado. */
  tieneFibra?: boolean;
  /**
   * Técnico: metros de FIBRA PLANA cargados en Materiales (la "Cantidad" que se
   * escribe al seleccionar cada serie). Si viene, el campo "Fibra utilizada" se
   * sincroniza con ese valor y no se escribe a mano.
   */
  metrosUtilizados?: number | null;
  /** Sin edición (traslado finalizado o pantalla de solo lectura). */
  bloqueado?: boolean;
  /** Técnico: avisa si hay metros sin guardar (para la validación de cierre). */
  onDirtyChange?: (dirty: boolean) => void;
  /** Se llama después de cada guardado exitoso del cuadro. */
  onGuardado?: () => void;
  title?: string;
};

const fecha = (v?: string) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "");
const esFactura = (tipo?: string | null) => `${tipo || ""}`.toUpperCase() === "FACTURA";
const aNumero = (v: string) => {
  const n = Number(`${v}`.replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
};

/** Chip con el estado del excedente guardado. */
function ChipEstado({ excedente }: { excedente: ExcedenteFibra | null }) {
  if (!excedente) return null;
  if (excedente.estado === "PENDIENTE") {
    return <Chip size="small" color="warning" variant="outlined" label="Pendiente de aprobación (Activaciones)" />;
  }
  if (excedente.estado === "APROBADO") {
    return (
      <Chip
        size="small"
        color="success"
        label={`Aprobado por Activaciones${excedente.aprobado_por ? ` · ${excedente.aprobado_por}` : ""}`}
      />
    );
  }
  return <Chip size="small" color="success" variant="outlined" label="Sin cobro" />;
}

/** Resumen de lo guardado: metros, lo que se cobra y cómo. */
function ResumenExcedente({ excedente }: { excedente: ExcedenteFibra }) {
  const cobra = (excedente.metros_cobrados || 0) > 0;
  return (
    <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "rgba(0,105,92,0.06)" }}>
      <Typography variant="body2">
        Fibra utilizada: <b>{excedente.metros} m</b> · Gratis: <b>{excedente.metros_gratis} m</b> · Excedente:{" "}
        <b>{excedente.metros_cobrados || 0} m</b>
      </Typography>
      {cobra ? (
        excedente.estado === "APROBADO" ? (
          <Typography variant="body2" fontWeight={700} sx={{ color: ACCENT }}>
            {excedente.metros_cobrados} m × {moneyExcedente(excedente.precio_metro_sin_iva)} ={" "}
            {moneyExcedente(excedente.valor_sin_iva)} + IVA {moneyExcedente(excedente.iva)} · Total:{" "}
            {moneyExcedente(excedente.valor_total)}
            {excedente.tipo_pago ? ` · ${excedente.tipo_pago}` : ""}
            {esFactura(excedente.tipo_pago) && excedente.meses_pago ? ` (${excedente.meses_pago} mes(es))` : ""}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            El costo y el precio los define Activaciones al aprobar.
          </Typography>
        )
      ) : (
        <Typography variant="body2" color="text.secondary">
          Sin excedente: no supera los metros gratis, no genera cobro ni requiere aprobación.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
        {excedente.registrado_por
          ? `Registrado por ${excedente.registrado_por}${excedente.registrado_fecha ? ` · ${fecha(excedente.registrado_fecha)}` : ""}`
          : ""}
        {excedente.aprobado_por
          ? `${excedente.registrado_por ? " — " : ""}Validado por ${excedente.aprobado_por}${excedente.aprobado_fecha ? ` · ${fecha(excedente.aprobado_fecha)}` : ""}`
          : ""}
      </Typography>
    </Box>
  );
}

/**
 * "Fibra utilizada" del traslado (va debajo de "Puntos de red (cable UTP)").
 *
 *  - `tecnico`: aparece si agregó FIBRA PLANA en materiales. Los metros se toman
 *    de la "Cantidad" cargada por serie en Materiales (`metrosUtilizados`) y se
 *    registran; si superan los gratis, el EXCEDENTE queda pendiente de Activaciones.
 *  - `activaciones`: valida los metros, define precio y forma de pago, y aprueba.
 *  - `lectura`: solo muestra lo guardado.
 *
 * Guarda con su propio endpoint (no con el botón de conceptos): el backend
 * calcula los montos (excedente = metros − gratis) y devuelve el estado en vivo.
 */
export default function ExcedenteFibraTraslado({
  idOrdenTrabajo,
  excedente,
  modo,
  tieneFibra = false,
  metrosUtilizados = null,
  bloqueado = false,
  onDirtyChange,
  onGuardado,
  title = "Fibra utilizada",
}: Props) {
  const { excedenteFibraTraslado: config, ivaPorcentaje, metodosPago } = usePreciosConceptosVisita("TRASLADO");
  const setEstadoLive = useSetFotosTrasladoLive();
  const refrescarLive = useRefrescarFotosTrasladoLive();
  const [guardando, setGuardando] = useState(false);

  // --- Campos editables, sincronizados con lo guardado (cambia en vivo). ---
  const [metros, setMetros] = useState<string>("");
  const [precio, setPrecio] = useState<string>("");
  const [tipoPago, setTipoPago] = useState<string>("");
  const [meses, setMeses] = useState<string>("1");
  const syncKey = JSON.stringify([
    excedente?.metros ?? null,
    excedente?.precio_metro_sin_iva ?? null,
    excedente?.tipo_pago ?? null,
    excedente?.meses_pago ?? null,
    excedente?.estado ?? null,
  ]);
  // Técnico con fibra en Materiales: el campo sigue a los metros cargados ahí.
  const sincronizado = modo === "tecnico" && metrosUtilizados != null;
  useEffect(() => {
    setMetros(
      sincronizado
        ? String(metrosUtilizados)
        : excedente?.metros != null
          ? String(excedente.metros)
          : "",
    );
    setPrecio(
      excedente?.precio_metro_sin_iva != null ? String(excedente.precio_metro_sin_iva) : String(config.precio_metro_sin_iva),
    );
    setTipoPago(excedente?.tipo_pago || "");
    setMeses(String(excedente?.meses_pago || 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncKey]);
  useEffect(() => {
    if (sincronizado) setMetros(String(metrosUtilizados));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sincronizado, metrosUtilizados]);

  const metrosNum = metros.trim() === "" ? 0 : aNumero(metros);
  const metrosValidos = Number.isFinite(metrosNum) && metrosNum >= 0;
  const metrosGuardados = Number(excedente?.metros) || 0;
  const dirtyMetros = metrosValidos && metrosNum !== metrosGuardados;

  useEffect(() => {
    if (modo === "tecnico") onDirtyChange?.(dirtyMetros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirtyMetros, modo]);
  // Al salir de la pestaña el valor escrito se descarta: ya no hay nada "sin guardar".
  useEffect(
    () => () => {
      onDirtyChange?.(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const metrosGratis = excedente?.metros_gratis ?? config.metros_gratis;
  const precioNum = precio.trim() === "" ? 0 : aNumero(precio);
  const precioValido = Number.isFinite(precioNum) && precioNum >= 0;
  // Vista previa (el valor definitivo lo calcula el backend con la misma regla).
  const previa = calcularExcedenteFibra(
    metrosValidos ? metrosNum : 0,
    { metros_gratis: metrosGratis, precio_metro_sin_iva: precioValido ? precioNum : 0 },
    excedente?.iva_porcentaje ?? ivaPorcentaje,
  );

  const ejecutar = async (accion: () => Promise<any>, mensajeOk: string) => {
    if (!idOrdenTrabajo) return;
    setGuardando(true);
    try {
      const snapshot = await accion();
      setEstadoLive(snapshot);
      toast.success(mensajeOk);
      onGuardado?.();
    } catch (e) {
      // El motivo ya lo mostró el cliente HTTP; se refresca el estado en vivo.
      console.error("Error al guardar el excedente de fibra", e);
      refrescarLive(idOrdenTrabajo);
    } finally {
      setGuardando(false);
    }
  };

  const encabezado = (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
      <CableOutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
      <ChipEstado excedente={excedente} />
    </Stack>
  );

  // ---------------- SOLO LECTURA ----------------
  if (modo === "lectura" || bloqueado) {
    if (!excedente) return null;
    return (
      <Box>
        {encabezado}
        <ResumenExcedente excedente={excedente} />
      </Box>
    );
  }

  // ---------------- TÉCNICO ----------------
  if (modo === "tecnico") {
    // Igual que el cable UTP: la opción aparece al agregar la fibra en materiales.
    if (!tieneFibra && !excedente) return null;
    const guardar = () =>
      ejecutar(
        () => registrarExcedenteFibra(idOrdenTrabajo!, metrosNum),
        metrosNum === 0
          ? "Fibra utilizada quitada."
          : previa.metros_cobrados > 0
            ? `Fibra utilizada registrada. Excedente de ${previa.metros_cobrados} m pendiente de aprobación por Activaciones.`
            : "Fibra utilizada registrada (sin excedente).",
      );
    const origen = sincronizado
      ? "Tomado de la FIBRA PLANA cargada en Materiales (cantidad por serie). "
      : "";
    // Texto de ayuda bajo el campo: lo guardado (si no hay cambios) o la vista
    // previa de lo que hay cargado.
    const ayudaMetros = !metrosValidos
      ? "Ingresa un número de metros válido."
      : !dirtyMetros && excedente
        ? excedente.estado === "APROBADO"
          ? `${origen}Excedente de ${excedente.metros_cobrados} m aprobado por Activaciones.`
          : excedente.estado === "PENDIENTE"
            ? `${origen}Excedente de ${excedente.metros_cobrados} m sobre los ${metrosGratis} m gratis: pendiente de aprobación de Activaciones.`
            : `${origen}Dentro de los ${metrosGratis} m gratis: sin excedente.`
        : metros.trim() === ""
          ? sincronizado
            ? "Carga las series de la FIBRA PLANA en Materiales para tomar los metros."
            : "Ingresa los metros de fibra utilizada."
          : previa.metros_cobrados > 0
            ? `${origen}Excedente de ${previa.metros_cobrados} m sobre los ${metrosGratis} m gratis: requiere aprobación de Activaciones.`
            : `${origen}Dentro de los ${metrosGratis} m gratis: sin excedente.`;
    return (
      <Box>
        {encabezado}
        {!tieneFibra && excedente && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Hay fibra utilizada registrada pero ya no está la <b>FIBRA PLANA</b> en los Materiales del
            traslado. Vuelve a agregarla o deja los metros en 0.
          </Alert>
        )}
        <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
          Los metros de fibra utilizada se toman de la <b>Cantidad</b> cargada por serie de la FIBRA PLANA en
          Materiales. Los primeros <b>{metrosGratis} m</b> no tienen costo. Si los supera, el <b>excedente</b> lo
          gestiona <b>Activaciones</b> en costo y precio, y deberán aprobarlo para que puedas finalizar el traslado.
        </Alert>
        <Grid container spacing={1.5} alignItems="flex-start">
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Fibra utilizada (metros)"
              inputProps={{ min: 0, step: 1, inputMode: "decimal", readOnly: sincronizado }}
              value={metros}
              onChange={(e) => {
                if (!sincronizado) setMetros(e.target.value);
              }}
              error={!metrosValidos}
              helperText={ayudaMetros}
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <LoadingButton
              variant="contained"
              color="secondary"
              loading={guardando}
              disabled={!dirtyMetros || !metrosValidos}
              onClick={guardar}
              sx={{ minHeight: 40 }}
            >
              Guardar fibra utilizada
            </LoadingButton>
          </Grid>
          {!dirtyMetros && excedente && (
            <Grid item xs={12} sm="auto" sx={{ display: "flex", alignItems: "center", minHeight: 40 }}>
              <Typography variant="caption" color="success.main">
                ✓ Guardado
              </Typography>
            </Grid>
          )}
        </Grid>
        {metrosValidos && previa.metros_cobrados > 0 && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            <b>Excedente: {previa.metros_cobrados} m</b> — los {metrosNum} m utilizados superan los {metrosGratis} m
            gratis.{dirtyMetros ? " Guarda la fibra utilizada para que Activaciones lo apruebe." : ""}
          </Alert>
        )}
        {excedente && (
          <Box sx={{ mt: 1.5 }}>
            <ResumenExcedente excedente={excedente} />
          </Box>
        )}
      </Box>
    );
  }

  // ---------------- ACTIVACIONES ----------------
  if (!excedente) {
    return (
      <Box>
        {encabezado}
        <Typography variant="body2" color="text.secondary">
          El técnico no ha registrado la fibra utilizada.
        </Typography>
      </Box>
    );
  }

  const pendiente = excedenteFibraPendiente(excedente);
  const hayCobro = previa.valor_total > 0;
  const dirtyActivaciones =
    dirtyMetros ||
    (precioValido && precioNum !== (Number(excedente.precio_metro_sin_iva) || 0)) ||
    (hayCobro && (tipoPago || "") !== (excedente.tipo_pago || "")) ||
    (hayCobro && esFactura(tipoPago) && Number(meses) !== (Number(excedente.meses_pago) || 1));
  const faltaFormaPago = hayCobro && !tipoPago;
  const puedeAprobar =
    metrosValidos && metrosNum > 0 && precioValido && !faltaFormaPago && (pendiente || dirtyActivaciones);
  const opcionesPago = tipoPago && !metodosPago.includes(tipoPago) ? [tipoPago, ...metodosPago] : metodosPago;

  const aprobar = () =>
    ejecutar(
      () =>
        aprobarExcedenteFibra(idOrdenTrabajo!, {
          metros: metrosNum,
          precio_metro_sin_iva: precioNum,
          tipo_pago: hayCobro ? tipoPago : null,
          meses_pago: hayCobro && esFactura(tipoPago) ? Math.max(1, Number(meses) || 1) : null,
        }),
      previa.metros_cobrados > 0 ? "Excedente de fibra aprobado." : "Excedente de fibra guardado (sin cobro).",
    );

  return (
    <Box>
      {encabezado}
      {pendiente && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          El técnico{excedente.registrado_por ? ` (${excedente.registrado_por})` : ""} registró{" "}
          <b>{excedente.metros} m</b> de fibra utilizada: supera los <b>{excedente.metros_gratis} m</b> gratis,{" "}
          <b>excedente de {excedente.metros_cobrados} m</b>. Valida los metros, define el precio y la forma de pago,
          y aprueba. Hasta entonces el técnico no puede finalizar el traslado.
        </Alert>
      )}
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Fibra utilizada (metros)"
            inputProps={{ min: 0, step: 1 }}
            value={metros}
            onChange={(e) => setMetros(e.target.value)}
            error={!metrosValidos || metrosNum <= 0}
            helperText={`Gratis: ${metrosGratis} m · Excedente: ${previa.metros_cobrados} m`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Precio por metro (sin IVA)"
            inputProps={{ min: 0, step: 0.01 }}
            value={precio}
            onChange={(e) => setPrecio(e.target.value)}
            error={!precioValido}
            helperText={`Catálogo: ${moneyExcedente(config.precio_metro_sin_iva)}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            fullWidth
            size="small"
            label="Forma de pago"
            value={hayCobro ? tipoPago : ""}
            onChange={(e) => setTipoPago(e.target.value)}
            disabled={!hayCobro}
            error={faltaFormaPago}
            helperText={hayCobro ? (faltaFormaPago ? "Selecciona la forma de pago." : " ") : "Sin valor a cobrar."}
          >
            {opcionesPago.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {hayCobro && esFactura(tipoPago) && (
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Meses a diferir"
              inputProps={{ min: 1, step: 1 }}
              value={meses}
              onChange={(e) => setMeses(e.target.value)}
              helperText=" "
            />
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 0.5, p: 1.25, borderRadius: 1.5, bgcolor: "rgba(0,105,92,0.06)" }}>
        <Typography variant="body2">
          {previa.metros_cobrados} m × {moneyExcedente(precioValido ? precioNum : 0)} ={" "}
          <b>{moneyExcedente(previa.valor_sin_iva)}</b> + IVA <b>{moneyExcedente(previa.iva)}</b>
        </Typography>
        <Typography variant="body2" fontWeight={700} sx={{ color: ACCENT }}>
          Total a cobrar (con IVA): {moneyExcedente(previa.valor_total)}
        </Typography>
        {previa.metros_cobrados > 0 && !hayCobro && (
          <Typography variant="caption" color="text.secondary" display="block">
            Con precio $0 el excedente se aprueba sin cobro. Si corresponde cobrar, indica el precio por metro.
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
          {excedente.registrado_por
            ? `Registrado por ${excedente.registrado_por}${excedente.registrado_fecha ? ` · ${fecha(excedente.registrado_fecha)}` : ""}`
            : ""}
          {excedente.aprobado_por
            ? ` — Validado por ${excedente.aprobado_por}${excedente.aprobado_fecha ? ` · ${fecha(excedente.aprobado_fecha)}` : ""}`
            : ""}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mt: 1.5 }}>
        {!pendiente && !dirtyActivaciones && (
          <Typography variant="caption" color="success.main">
            ✓ {excedente.estado === "APROBADO" ? "Aprobado" : "Sin cobro"}
          </Typography>
        )}
        <LoadingButton
          variant="contained"
          color={pendiente ? "warning" : "secondary"}
          loading={guardando}
          disabled={!puedeAprobar}
          onClick={aprobar}
        >
          {previa.metros_cobrados > 0 ? "Aprobar excedente" : "Guardar excedente (sin cobro)"}
        </LoadingButton>
      </Box>
    </Box>
  );
}
